import { useEffect, useMemo } from 'react';
import { PageDto } from '@asset-metrix/portal-gateway-client-js';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../store';
import { getGlobalSearchFiltersFromPage } from '../utils/getGlobalSearchFiltersFromPage';

import { presetFilterSelectionForFilter } from './presetFilterSelectionForFilter';
import { useSearchParamsByKeys } from './useSearchParamsByKey';

export type UsePresetFiltersFromUrlParams = {
  pageData?: PageDto;
  instanceId: string | null;
};

// pre-set page filters from url
export const usePresetPageFiltersFromUrl = ({
  pageData,
  instanceId,
}: UsePresetFiltersFromUrlParams) => {
  const [filterData, presetFilterSelection, setFilterInitialised] = useStore(
    useShallow((state) => [
      state.filterData,
      state.presetFilterSelection,
      state.setFilterInitialised,
    ])
  );

  const config = pageData?.pageFilter;
  const filterKeys = config?.filters.map((f) => f.key) ?? [];

  // special handling for global search
  // the global search filter is a table filter, but will be handled globally for all widgets
  // on a page
  const globalSearchKeys = useMemo(() => {
    return pageData != null ? getGlobalSearchFiltersFromPage(pageData) : [];
  }, [pageData]);

  const filtersFromUrl = useSearchParamsByKeys(
    globalSearchKeys.length > 0 ? [...filterKeys, 'global_search'] : filterKeys
  );

  useEffect(() => {
    // check if filters have already been set and entity id is defined
    if (
      instanceId == null ||
      filterData[instanceId]?.meta.initialised === true ||
      pageData == null
    ) {
      // already initialised or config not yet loaded, nothing to do
      return;
    }

    // preset configured page filters
    for (const fc of config?.filters ?? []) {
      presetFilterSelectionForFilter(
        presetFilterSelection,
        instanceId,
        filtersFromUrl[fc.key],
        fc
      );
    }

    // special handling for global search
    if (globalSearchKeys.length > 0 && filtersFromUrl.global_search != null) {
      for (const { widgetId, metadataId } of globalSearchKeys) {
        presetFilterSelection(
          widgetId,
          metadataId,
          'search',
          'text',
          filtersFromUrl.global_search[0]
        );
      }
    }

    // finally, mark filters as initialised in any case
    setFilterInitialised(instanceId);
  }, [
    instanceId,
    config?.filters,
    filterData,
    filtersFromUrl,
    globalSearchKeys,
    pageData,
    presetFilterSelection,
    setFilterInitialised,
  ]);
};
